import { QueryClient } from '@tanstack/react-query';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const WS_PROTOCOL = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
const WS_BASE_URL = API_BASE_URL.replace(/^https?/, WS_PROTOCOL);
const PING_INTERVAL = 30000; // 30 seconds

// Define event types
export type WebSocketEventType = 
  | 'booking_update'
  | 'event_update'
  | 'court_update'
  | 'facility_update'
  | 'court_availability_update';

// Define event handlers
type EventHandler = (data: any) => void;

class WebSocketService {
    private ws: WebSocket | null = null;
    private queryClient: QueryClient | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectTimeout = 1000; // Start with 1 second
    private currentFacilityId: string | null = null;
    private currentBusinessId: string | null = null;
    private currentEndpoint: string | null = null;
    private pingInterval: NodeJS.Timeout | null = null;
    private isConnecting = false;
    private isDisconnecting = false; // Add flag to track intentional disconnects
    private eventHandlers: Map<WebSocketEventType, Set<EventHandler>> = new Map();
    private subscriptions: Set<WebSocketEventType> = new Set();

    constructor() {
        this.handleMessage = this.handleMessage.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleOpen = this.handleOpen.bind(this);
        this.handleError = this.handleError.bind(this);
        console.log('WebSocketService initialized');
    }

    setQueryClient(queryClient: QueryClient) {
        console.log('Setting QueryClient in WebSocketService');
        this.queryClient = queryClient;
    }

    // Subscribe to specific event types
    subscribe(eventType: WebSocketEventType, handler: EventHandler) {
        console.log(`Subscribing to ${eventType} events`);
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, new Set());
        }
        this.eventHandlers.get(eventType)?.add(handler);
        this.subscriptions.add(eventType);
        
        // If we're already connected, send subscription message
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.sendSubscriptionUpdate();
        }
    }

    // Unsubscribe from specific event types
    unsubscribe(eventType: WebSocketEventType, handler: EventHandler) {
        console.log(`Unsubscribing from ${eventType} events`);
        this.eventHandlers.get(eventType)?.delete(handler);
        if (this.eventHandlers.get(eventType)?.size === 0) {
            this.eventHandlers.delete(eventType);
            this.subscriptions.delete(eventType);
        }
        
        // If we're connected, send updated subscription message
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.sendSubscriptionUpdate();
        }
    }

    private sendSubscriptionUpdate() {
        if (this.ws?.readyState === WebSocket.OPEN) {
            const message = {
                type: 'subscription_update',
                subscriptions: Array.from(this.subscriptions)
            };
            console.log('Sending subscription update:', message);
            this.ws.send(JSON.stringify(message));
        }
    }

    private startPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
        }
        this.pingInterval = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                console.log('Sending ping to keep connection alive');
                this.ws.send(JSON.stringify({ type: 'ping' }));
            } else {
                console.log('WebSocket not open, skipping ping');
            }
        }, PING_INTERVAL);
    }

    private stopPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    connect(facilityId: string, businessId: string, endpoint: string = 'guest-bookings') {
        console.log('Attempting to connect WebSocket for business:', businessId, 'facility:', facilityId, 'endpoint:', endpoint);
        
        if (this.isConnecting) {
            console.log('Connection already in progress, skipping');
            return;
        }

        if (this.ws?.readyState === WebSocket.OPEN) {
            console.log('WebSocket already connected');
            return;
        }

        if (this.ws?.readyState === WebSocket.CONNECTING) {
            console.log('WebSocket connection in progress');
            return;
        }

        this.currentFacilityId = facilityId;
        this.currentBusinessId = businessId;
        this.currentEndpoint = endpoint;
        this.isConnecting = true;

        // Subscribe to booking updates by default
        this.subscriptions.add('booking_update');

        // Use the new WS_BASE_URL for WebSocket connection
        const wsUrl = WS_BASE_URL + `/ws/${endpoint}/${businessId}/${facilityId}`;
        console.log('Connecting to WebSocket URL:', wsUrl);

        try {
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = this.handleOpen;
            this.ws.onmessage = this.handleMessage;
            this.ws.onclose = this.handleClose;
            this.ws.onerror = this.handleError;
        } catch (error) {
            console.error('Error creating WebSocket:', error);
            this.isConnecting = false;
            // Add retry logic for connection errors
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                setTimeout(() => {
                    this.connect(facilityId, businessId, endpoint);
                }, this.reconnectTimeout * Math.pow(2, this.reconnectAttempts - 1));
            }
        }
    }

    private handleOpen(event: Event) {
        console.log('WebSocket connected successfully');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.startPingInterval();
        
        // Send current subscriptions to the server
        this.sendSubscriptionUpdate();
    }

    private handleMessage(event: MessageEvent) {
        console.log('Received WebSocket message:', event.data);
        try {
            const message = JSON.parse(event.data);
            console.log('Parsed WebSocket message:', message);
            
            // Handle different message types
            switch (message.type) {
                case 'booking_update':
                    this.handleEvent('booking_update', message.data);
                    break;
                case 'event_update':
                    this.handleEvent('event_update', message.data);
                    break;
                case 'court_update':
                    this.handleEvent('court_update', message.data);
                    break;
                case 'facility_update':
                    this.handleEvent('facility_update', message.data);
                    break;
                case 'court_availability_update':
                    this.handleEvent('court_availability_update', message.data);
                    break;
                case 'pong':
                    console.log('Received pong from server');
                    break;
                default:
                    console.log('Unknown message type:', message.type);
            }
        } catch (error) {
            console.error('Error handling WebSocket message:', error);
        }
    }

    private handleEvent(eventType: WebSocketEventType, data: any) {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`Error in ${eventType} event handler:`, error);
                }
            });
        }
    }

    private handleError(error: Event) {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        
        // Attempt to reconnect if we have a facility ID and endpoint
        if (this.currentFacilityId && this.currentBusinessId && this.currentEndpoint) {
            this.reconnectAttempts++;
            if (this.reconnectAttempts <= this.maxReconnectAttempts) {
                console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                setTimeout(() => {
                    this.connect(this.currentFacilityId!, this.currentBusinessId!, this.currentEndpoint!);
                }, this.reconnectTimeout * Math.pow(2, this.reconnectAttempts - 1)); // Exponential backoff
            } else {
                console.error('Max reconnection attempts reached');
            }
        }
    }

    private handleClose(event: CloseEvent) {
        console.log('WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }

        // Only attempt to reconnect if this wasn't an intentional disconnect
        if (!this.isDisconnecting && this.currentFacilityId && this.currentBusinessId && this.currentEndpoint) {
            this.reconnectAttempts++;
            if (this.reconnectAttempts <= this.maxReconnectAttempts) {
                console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                setTimeout(() => {
                    this.connect(this.currentFacilityId!, this.currentBusinessId!, this.currentEndpoint!);
                }, this.reconnectTimeout * Math.pow(2, this.reconnectAttempts - 1)); // Exponential backoff
            } else {
                console.error('Max reconnection attempts reached');
            }
        }
    }

    disconnect() {
        console.log('Disconnecting WebSocket');
        this.isDisconnecting = true; // Set flag before disconnecting
        this.stopPingInterval();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        // Don't clear currentFacilityId and currentEndpoint to allow reconnection
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        // Don't clear eventHandlers and subscriptions
        this.isDisconnecting = false; // Reset flag after disconnecting
    }

    // Add a method to force disconnect (for logout)
    forceDisconnect() {
        console.log('Force disconnecting WebSocket');
        this.isDisconnecting = true;
        this.stopPingInterval();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.currentFacilityId = null;
        this.currentBusinessId = null;
        this.currentEndpoint = null;
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.eventHandlers.clear();
        this.subscriptions.clear();
        this.isDisconnecting = false;
    }
}

// Create a singleton instance
export const websocketService = new WebSocketService(); 